import { Box, Button, Typography, useTheme } from '@mui/material';
import { BatchEditRenderValueElem } from 'components/BatchEdit/components/BatchEditRenderValueElem';
import { BatchEditSelectMenuItem } from 'components/BatchEdit/components/BatchEditSelectMenuItem';
import { flagsChoices, getSelectSx } from 'components/Inputs/Dropdown/dropdownConsts';
import { SelectDropdownInput } from 'components/Inputs/Dropdown/SelectDropdown/SelectDropdownInput';
import { SingleDateDropdown } from 'components/Inputs/Dropdown/SingleDateDropdownInput/SingleDateDropdownInput';
import { EnterTextDropdownInput } from 'components/Inputs/Dropdown/EnterTextDropdownInput/EnterTextDropdownInput';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';

import {
  useGetFetchAndFormatAuthors,
  useGetFetchAndFormatBatchEditWorkflows,
  useGetFetchAndFormatSections,
  useGetFetchAndFormatSiteTags,
  useGetFetchAndFormatKeywords,
} from 'components/Search/components/SearchAdvance/components/hooks';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  checkChangePublishStatusPermission,
  checkChangeWorkflowPermission,
  checkSiteAdminPermission,
} from 'utility/permissions';
import { useBatchEdit } from '../../context';
import { bloxValues } from 'themes/constants';
import { usePresentationTypes } from '../../hooks/usePresentationTypes';
import { useArticleTypes } from '../../hooks/useArticleTypes';
import { InputChoice } from 'components/Inputs/Dropdown/dropdownConsts';

export const AddRemove = memo(() => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { sessionConfig } = useCosmos();
  const {
    simpleValues,
    handleSelectionChange,
    handleClearSelection,
    addRemoveValues,
    updateAddRemoveValue,
    isDeletedSearch,
  } = useBatchEdit();

  // Get presentation types from CosmosEditorial context
  const {
    articlePresentationTypes,
    imagePresentationTypes,
    collectionPresentationTypes,
  } = usePresentationTypes();

  // Get article types from CosmosEditorial context
  const { articleTypes } = useArticleTypes();

  // Prepend "Remove" option to choices as needed
  const prependRemoveOption = useMemo(
    () =>
      (choices: InputChoice[]): InputChoice[] => [
        { label: t('blox.actions.remove'), value: '' },
        ...choices,
      ],
    [t],
  );

  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: canChangePublish } = checkChangePublishStatusPermission(sessionConfig);
  const { granted: canChangeWorkflow } = checkChangeWorkflowPermission(sessionConfig);

  // Determine permissions for protected actions
  const allowChangePublish = userIsAdmin || canChangePublish;
  const allowChangeWorkflow = userIsAdmin || canChangeWorkflow;

  // Fetch functions for dropdown data
  const { fetchAndFormatSections } = useGetFetchAndFormatSections();
  const { fetchAndFormatAuthors } = useGetFetchAndFormatAuthors();
  const { fetchAndFormatSiteTags } = useGetFetchAndFormatSiteTags();
  const { fetchAndFormatBatchEditWorkflows } = useGetFetchAndFormatBatchEditWorkflows();
  const { fetchAndFormatKeywords } = useGetFetchAndFormatKeywords();

  const dropdownConfigs = useMemo(() => {
    return [
      {
        label: t('blox.editorial.asset.properties.no_publish.label_alt'),
        group: 'property_no_publish',
        value: simpleValues.property_no_publish ?? [],
        multiple: false,
        showFilter: false,
        has_permission: allowChangePublish,
        staticChoices: [
          {
            label: t('blox.editorial.asset.properties.no_publish.help.allow'),
            value: 'ok',
          }, // Filler value for select/chip, transformed to '' before submit
          { label: t('blox.editorial.asset.properties.no_publish.label'), value: '1' },
        ],
        removeButtonText: t('blox.actions.clear'),
      },
      {
        label: t('blox.editorial.asset.properties.workflow.label'),
        group: 'workflow_process',
        value: simpleValues.workflow_process,
        multiple: false,
        has_permission: allowChangeWorkflow,
        fetchDataFn: fetchAndFormatBatchEditWorkflows,
        removeButtonText: t('blox.actions.clear'),
        showUnlabeledItems: false,
      },
      {
        label: t('blox.editorial.asset.sections.label'),
        group: 'sections',
        value: simpleValues.sections,
        fetchDataFn: fetchAndFormatSections,
      },
      {
        label: t('blox.editorial.asset.keywords.label'),
        group: 'tag_keyword',
        value: simpleValues.tag_keyword,
        fetchDataFn: fetchAndFormatKeywords,
        removeButtonText: t('blox.actions.clear'),
        enableCycleSelection: true,
        filterPlaceholderText: t('blox.editorial.asset.keywords.help'),
        noResultsMessage: t('blox.editorial.asset.keywords.not_found'),
      },
      {
        label: t('blox.editorial.asset.flags.label'),
        group: 'flags',
        value: simpleValues.flags,
        staticChoices: flagsChoices,
      },
      {
        label: t('blox.editorial.asset.site_tags.label'),
        group: 'site_tags',
        value: simpleValues.site_tags,
        fetchDataFn: fetchAndFormatSiteTags,
      },
      {
        label: t('blox.editorial.asset.authors.label'),
        group: 'authors',
        value: Array.isArray(simpleValues.authors)
          ? simpleValues.authors
          : simpleValues.authors
            ? [simpleValues.authors]
            : undefined,
        staticChoices: addRemoveValues?.authors,
        fetchDataFn: fetchAndFormatAuthors,
      },
      // Presentation types dropdowns (merged in context to one batch value for API if set)
      {
        label: `${t('blox.editorial.asset.presentation.label')} : ${t(
          'blox.editorial.article.label',
        )}`,
        group: 'presentation_article',
        value: simpleValues.presentation_article ?? [],
        staticChoices: prependRemoveOption(articlePresentationTypes),
        multiple: false,
        removeButtonText: t('blox.actions.clear'),
      },
      {
        label: `${t('blox.editorial.asset.presentation.label')} : ${t(
          'blox.editorial.image.label',
        )}`,
        group: 'presentation_image',
        value: simpleValues.presentation_image ?? [],
        staticChoices: prependRemoveOption(imagePresentationTypes),
        multiple: false,
        removeButtonText: t('blox.actions.clear'),
      },
      {
        label: `${t('blox.editorial.asset.presentation.label')} : ${t(
          'blox.editorial.collection.label',
        )}`,
        group: 'presentation_collection',
        value: simpleValues.presentation_collection ?? [],
        staticChoices: prependRemoveOption(collectionPresentationTypes),
        multiple: false,
        removeButtonText: t('blox.actions.clear'),
      },
      // Article type dropdown
      {
        label: t('blox.editorial.asset.properties.article_type.label'),
        group: 'property_article_type',
        value: simpleValues.property_article_type ?? [],
        staticChoices: prependRemoveOption(articleTypes),
        multiple: false,
        removeButtonText: t('blox.actions.clear'),
      },
      // Photo sales dropdown
      {
        label: t('blox.editorial.asset.properties.photo_sales.label'),
        group: 'photo_sales',
        value: simpleValues.photo_sales ?? [],
        staticChoices: [
          {
            label: t('blox.editorial.image.photo_sales.options.automatic'),
            value: '',
          },
          {
            label: t('blox.general.enabled'),
            value: 'enable',
          },
          {
            label: t('blox.general.disabled'),
            value: 'disable',
          },
        ],
        removeButtonText: t('blox.actions.clear'),
        multiple: false,
      },
    ].filter((item) => item.has_permission !== false);
  }, [
    articleTypes,
    articlePresentationTypes,
    imagePresentationTypes,
    collectionPresentationTypes,
    simpleValues,
    allowChangePublish,
    allowChangeWorkflow,
    t,
    addRemoveValues?.authors,
    fetchAndFormatSections,
    fetchAndFormatSiteTags,
    fetchAndFormatAuthors,
    fetchAndFormatBatchEditWorkflows,
    fetchAndFormatKeywords,
    prependRemoveOption,
  ]);

  const textDropdownConfigs = useMemo(() => {
    return [
      {
        label: t('blox.editorial.asset.byline.label'),
        field: 'property_byline',
        value: Array.isArray(simpleValues.property_byline)
          ? simpleValues.property_byline[0]
          : (simpleValues.property_byline ?? null),
        placeholder: t('blox.editorial.asset.byline.help'),
        textArea: true,
      },
      {
        label: t('blox.editorial.metadata.group.print_slug.label'),
        field: 'property_print_slug',
        value: Array.isArray(simpleValues.property_print_slug)
          ? simpleValues.property_print_slug[0]
          : (simpleValues.property_print_slug ?? null),
        placeholder: t('blox.editorial.metadata.group.print_slug.placeholder'),
      },
      {
        label: t('blox.editorial.asset.properties.priority.label'),
        field: 'property_priority',
        value: Array.isArray(simpleValues.property_priority)
          ? simpleValues.property_priority[0]
          : (simpleValues.property_priority ?? null),
        placeholder: t('blox.editorial.asset.properties.priority.placeholder'),
        numeric: true,
      },
    ];
  }, [
    simpleValues.property_priority,
    simpleValues.property_print_slug,
    simpleValues.property_byline,
    t,
  ]);

  const dateDropdownConfigs = useMemo(() => {
    return [
      {
        label: t('blox.editorial.asset.start_time.label'),
        field: 'start_time',
        value: Array.isArray(simpleValues.start_time)
          ? simpleValues.start_time[0]
          : (simpleValues.start_time ?? ''),
      },
      {
        label: t('blox.editorial.asset.archive_time.label'),
        field: 'archive_time',
        value: Array.isArray(simpleValues.archive_time)
          ? simpleValues.archive_time[0]
          : (simpleValues.archive_time ?? ''),
      },
      {
        label: t('blox.editorial.asset.delete_time.label'),
        field: 'delete_time',
        value: Array.isArray(simpleValues.delete_time)
          ? simpleValues.delete_time[0]
          : (simpleValues.delete_time ?? ''),
      },
    ];
  }, [t, simpleValues.start_time, simpleValues.delete_time, simpleValues.archive_time]);

  return (
    <Box
      data-testid="add-remove-wrapper"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {isDeletedSearch && (
        <Box
          data-testid="batch-edit-undo-deletion-section"
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            p: 1.5,
            borderRadius: 1,
            backgroundColor: theme.palette.action.hover,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            data-testid="batch-edit-undo-deletion-btn"
            variant="outlined"
            size="small"
            startIcon={<RestoreFromTrashIcon />}
            onClick={() => handleSelectionChange('delete_time', '__REMOVE__')}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {t('blox.batch_edit.actions.undo_deletion')}
          </Button>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              minWidth: 0,
              wordBreak: 'break-word',
            }}
          >
            {t('blox.batch_edit.actions.undo_deletion_help')}
          </Typography>
        </Box>
      )}
      {dropdownConfigs.map(
        ({
          label,
          group,
          value,
          fetchDataFn,
          staticChoices,
          multiple = true,
          showFilter = true,
          removeButtonText,
          enableCycleSelection = false,
          filterPlaceholderText,
          noResultsMessage,
          showUnlabeledItems,
        }) => (
          <Box
            key={group}
            data-testid="input-group"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: bloxValues.regularFontSizeRem,
                fontWeight: bloxValues.fontWeightMedium,
                color: theme.palette.text.primary,
              }}
            >
              {label}
            </Typography>
            <SelectDropdownInput
              // custom queryKey to not clash with other dropdowns
              // eg: fetchAndFormatBatchEditWorkflows with headers/group
              // so for react-query to not pull from the incorrect cache
              // eg regular search dropdowns with same inputLabel
              // we must pass a more specific queryKey here for uniqueness
              queryOptions={{
                // React Query v5 requires queryKey as an array; include group for uniqueness
                queryKey: [group, 'batch-select-input'],
              }}
              group={group}
              inputLabel={label}
              value={value as string[]}
              updateValue={(event, values) => handleSelectionChange(group, values)}
              clearSelection={(event) => handleClearSelection(group)}
              fetchDataFn={fetchDataFn}
              staticChoices={staticChoices}
              popoverMatchesFormControlWidth
              renderValueElm={BatchEditRenderValueElem}
              renderMenuItem={BatchEditSelectMenuItem}
              renderValueDisplay="chips"
              formControlVariant="outlined"
              inputPlaceholderText="stringify-options"
              popoverPaperSx={{ maxHeight: 500 }}
              showFilter={showFilter}
              multiple={multiple}
              removeButtonText={removeButtonText}
              enableCycleSelection={enableCycleSelection}
              filterPlaceholderText={filterPlaceholderText}
              noResultsMessage={noResultsMessage}
              showChipBox={false}
              showUnlabeledItems={showUnlabeledItems}
              addRemoveValues={addRemoveValues}
              updateAddRemoveValue={updateAddRemoveValue}
              selectSx={getSelectSx}
            />
          </Box>
        ),
      )}
      {textDropdownConfigs.map(
        ({ label, field, value, placeholder, numeric, textArea }) => (
          <Box
            key={field}
            data-testid="input-group"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: bloxValues.regularFontSizeRem,
                fontWeight: bloxValues.fontWeightMedium,
                color: theme.palette.text.primary,
              }}
            >
              {label}
            </Typography>
            <EnterTextDropdownInput
              label={label}
              value={
                typeof value === 'string' ? value : value != null ? String(value) : null
              }
              placeholder={placeholder}
              onChange={(newValue) => {
                if (newValue === null) {
                  handleClearSelection(field);
                } else {
                  handleSelectionChange(field, newValue);
                }
              }}
              numeric={numeric ?? false}
              textArea={textArea ?? false}
            />
          </Box>
        ),
      )}
      {dateDropdownConfigs.map(({ label, field, value }) => (
        <Box
          key={field}
          data-testid="input-group"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: bloxValues.regularFontSizeRem,
              fontWeight: bloxValues.fontWeightMedium,
              color: theme.palette.text.primary,
            }}
          >
            {label}
          </Typography>
          <SingleDateDropdown
            label={label}
            value={value}
            onChange={(newValue) => {
              if (newValue === null) {
                handleClearSelection(field);
              } else {
                handleSelectionChange(field, newValue);
              }
            }}
            disabled={false}
            selectSx={getSelectSx}
          />
        </Box>
      ))}
    </Box>
  );
});

AddRemove.displayName = 'AddRemove';
export default AddRemove;
